import { Component, effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, take } from 'rxjs';
import IncidentService from 'src/app/shared/services/incident.service';
import { IncidentCreatedBy, IncidentStatus } from '../../incident-models';
import { IncidentCreateOrEditModalComponent } from '../incident-create-or-edit-modal/incident-create-or-edit-modal.component';
import { EnvironmentLoaderService } from '../../../../shared/services/environment-loader.service';
import { IncidentDownloadReportComponent } from '../incident-download-report-dialog/incident-download-report-dialog.component';
import { AppState } from 'src/app/app.state';
import { DomSanitizer } from '@angular/platform-browser';
import { Organization } from 'src/app/features/organization/organization-models';
import OrganizationService from 'src/app/shared/services/organization.service';
import { RequiresFeatureRoleDirective } from '../../../../shared/directives/requires-feature-role.directive';
import { IncidentListComponent } from '../incident-list/incident-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { IncidentListStateService, IncidentsListQueryParams } from '../incident-list.state.service';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { TranslateIncidentStatusEnumPipe } from 'src/app/shared/pipes/translate-incident-status-enum.pipe';
import { TranslateIncidentCreatedByEnumPipe } from 'src/app/shared/pipes/translate-incident-created-by-enum.pipe';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'adm-incidents-overview',
  templateUrl: './incidents-overview.component.html',
  styleUrls: ['./incidents-overview.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    TextSearchComponent,
    IncidentListComponent,
    AdmOrgTypeaheadComponent,
    AdminEnumSelectListComponent,
    MatButton,
  ],
  providers: [IncidentListStateService, TranslateIncidentStatusEnumPipe, TranslateIncidentCreatedByEnumPipe],
})
export class IncidentsOverviewComponent {
  dialog = inject(MatDialog);
  incidentService = inject(IncidentService);
  activatedRoute = inject(ActivatedRoute);
  environmentLoaderService = inject(EnvironmentLoaderService);
  appState = inject(AppState);
  sanitizer = inject(DomSanitizer);
  organizationService = inject(OrganizationService);
  router = inject(Router);
  state = inject(IncidentListStateService);
  public roles = this.environmentLoaderService.virtualRoles;
  statusPipe = inject(TranslateIncidentStatusEnumPipe);
  createdByPipe = inject(TranslateIncidentCreatedByEnumPipe);

  constructor() {
    effect(() => {
      const filters = this.state.filters();
      const queryParams: Record<IncidentsListQueryParams, string | null> = {
        [IncidentsListQueryParams.organizationId]: filters.organizationId ? filters.organizationId.toString() : null,
        [IncidentsListQueryParams.query]: filters.query ? encodeURIComponent(filters.query) : null,
        [IncidentsListQueryParams.status]: filters.status ? filters.status.toString() : null,
        [IncidentsListQueryParams.createdBy]: filters.createdBy ? filters.createdBy.toString() : null,
      };

      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  updateStatus(value: IncidentStatus | null) {
    this.state.filters.update(f => ({ ...f, status: value, page: { ...f.page, pageIndex: 0 } }));
  }
  updateCreatedBy(value: IncidentCreatedBy | null) {
    this.state.filters.update(f => ({ ...f, createdBy: value, page: { ...f.page, pageIndex: 0 } }));
  }

  organizationSelected = (org: Organization | null) => {
    this.state.filters.update(x => ({
      ...x,
      organizationId: org ? org.id : null,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  public statusSelections: IncidentStatus[] = [IncidentStatus.Reported, IncidentStatus.Active, IncidentStatus.Closed];
  public createdBySelections: IncidentCreatedBy[] = [IncidentCreatedBy.System, IncidentCreatedBy.Customer, IncidentCreatedBy.OK];

  public openCreateIncidentDialog(): void {
    const dialogRef = this.dialog.open(IncidentCreateOrEditModalComponent, {
      data: {},
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.state.triggerUpdate();
        }
      });
  }

  public openDownloadIncidentReport(): void {
    this.dialog.open(IncidentDownloadReportComponent, {
      data: {},
      width: '660px',
    });
  }
}
